import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '../services/api';
import { AlertIcon, SparkIcon } from './Icons';

export function Spinner({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <span className="spinner" role="status">
      <span className="spinner__orbit" aria-hidden="true" />
      <SparkIcon size={16} />
      <span className="visually-hidden">{label ?? t('states.loading')}</span>
    </span>
  );
}

export function PageLoader() {
  return (
    <div className="page-loader">
      <Spinner />
    </div>
  );
}

export function EmptyState({ title, children, action, icon }: { title: string; children?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="empty-state">
      <span className="empty-state__icon" aria-hidden="true">
        {icon ?? <SparkIcon size={28} />}
      </span>
      <p className="empty-state__title">{title}</p>
      {children && <div className="empty-state__text">{children}</div>}
      {action}
    </div>
  );
}

export function SearchRequired({ onLoad }: { onLoad?: () => void }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      title={t('states.searchRequired')}
      action={onLoad ? <button type="button" className="button" onClick={onLoad}>{t('actions.showSelection')}</button> : undefined}
    >
      {t(onLoad ? 'states.loadSelectionHint' : 'states.searchRequiredHint')}
    </EmptyState>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="empty-state empty-state--error" role="alert">
      <span className="empty-state__icon" aria-hidden="true">
        <AlertIcon size={28} />
      </span>
      <p className="empty-state__title">{t('states.loadFailed')}</p>
      <p className="empty-state__text">{errorMessage(error)}</p>
      {onRetry && (
        <button type="button" className="button" onClick={onRetry}>
          {t('states.retry')}
        </button>
      )}
    </div>
  );
}

export function CardGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="card-grid" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="card-skeleton" />
      ))}
    </div>
  );
}
